/**
 * Notification contracts (docs/50 §4.9, docs/53 §7.4 and §7.7, docs/55 WP-NOTIFY). Added by WP-NOTIFY as a contracts
 * change so several packages agree on one shape:
 *
 * - `PushMessage`: the decrypted Web Push payload. `packages/notify` builds it, the PWA service worker (WP-PWA)
 *   parses it, and Safari 18.4+ shows it without the service worker (Declarative Web Push, `web_push: 8030`).
 * - `NOTIFY_TEMPLATES`: the variables each notification needs. WP-COPY's catalogue (`@ff/app-ui/copy`) holds the
 *   words; WP-JOBS builds the variables; `packages/notify` renders them. Transactional emails (sign in, invites)
 *   go through the `Mailer` contract in `mail.ts`.
 *
 * Template variables may hold a lead's name (docs/53 T-20 allows the name and service on a lock screen, never the
 * message or phone). They're built at send time from the data layer and never ride on a queue.
 */
import { z } from 'zod';
import { AppPath, IsoDateTime } from './common.ts';
import { NotificationEvent } from './api/account.ts';
import { DeliveryId, NotificationId } from './ids.ts';

// ---------------------------------------------------------------------------------------------- push payload

/** Absolute app URL for `navigate`: https in staging and production, http only for localhost in dev. */
const NavigateUrl = z
  .url({ protocol: /^https?$/ })
  .max(2048)
  .refine((u) => u.startsWith('https://') || /^http:\/\/(localhost|127\.0\.0\.1)(:\d+)?\//.test(u), 'navigate must be https (http only on localhost)');

/**
 * The plaintext inside every push (RFC 8291 encrypts it). Declarative Web Push shape (WebKit, Safari 18.4+): the
 * browser shows `notification` itself if the service worker is gone; `mutable: true` lets our service worker
 * replace it. `data` is what our service worker reads.
 */
export const PushMessage = z.object({
  web_push: z.literal(8030),
  notification: z.object({
    title: z.string().min(1).max(120),
    body: z.string().max(300),
    navigate: NavigateUrl,
    lang: z.literal('en-US'),
    dir: z.literal('ltr'),
    /** Same tag replaces the earlier notification on the device (tags are per subject). */
    tag: z.string().max(64),
    silent: z.boolean(),
    data: z.object({
      v: z.literal(1),
      event: NotificationEvent,
      /** Relative path the service worker opens (same as `navigate`, minus the origin). */
      path: AppPath,
      notificationId: NotificationId.nullable(),
      /** The service worker posts it to `deliveryOpened` on click, which cancels a waiting follow-up email. */
      deliveryId: DeliveryId,
      sentAt: IsoDateTime,
    }),
  }),
  mutable: z.literal(true),
});
export type PushMessage = z.infer<typeof PushMessage>;

// ---------------------------------------------------------------------------------------------- templates

const name = z.string().min(1).max(80);
const label = z.string().min(1).max(120);
const count = z.number().int().min(0).max(100_000);
/** "October" */
const monthLabel = z.string().min(3).max(20);
/** "Thursday, Oct 9", already formatted in the business's zone (docs/52 §7.3). */
const dateLabel = z.string().min(3).max(40);

/**
 * Variables for every notification, one entry per `NotificationEvent`. `path` is always a relative app path; the
 * renderer adds the origin.
 */
export const NOTIFY_TEMPLATES = {
  'lead.new': z.object({
    leadName: name,
    /** Service label and town; either may be missing on a short form. */
    service: label.nullable(),
    town: z.string().max(80).nullable(),
    /** "2:14 pm", in the recipient's zone. */
    receivedAt: z.string().min(3).max(20),
    path: AppPath,
  }),
  'review.new_high': z.object({ stars: z.number().int().min(4).max(5), reviewerName: name, path: AppPath }),
  'review.new_low': z.object({ stars: z.number().int().min(1).max(3), reviewerName: name, path: AppPath }),
  /** `reminder` 0 is the first ask; 1 and 2 are the 2 and 5 day reminders (docs/50 §4.9). */
  'approval.needed': z.object({ what: label, reminder: z.number().int().min(0).max(2), daysWaiting: z.number().int().min(0).max(365), path: AppPath }),
  'request.update': z.object({ what: label, stage: z.enum(['ready_for_you', 'live', 'replied']), requestNumber: z.number().int().positive(), path: AppPath }),
  /** `line` is the report's one-line headline ("Your map moved up."). */
  'report.ready': z.object({ month: monthLabel, line: label, path: AppPath }),
  'onboarding.reminder': z.object({ step: z.enum(['facts', 'google', 'domain', 'brand', 'photos', 'licence', 'go_live']), stepLabel: label, liveBy: dateLabel, path: AppPath }),
  'payment.failed': z.object({ month: monthLabel, updateBy: dateLabel, path: AppPath }),
  /** Normally sent by WP-AUTH through the Mailer (`auth.new_device`); here for routing and the settings screen. */
  'security.new_device': z.object({ when: z.string().min(3).max(60), where: z.string().min(1).max(120), path: AppPath }),
  'site.down': z.object({ host: z.string().min(3).max(253), path: AppPath }),
  'summary.weekly': z.object({ leads: count, reviews: count, jobs: count, path: AppPath }),
} as const satisfies Record<z.infer<typeof NotificationEvent>, z.ZodType>;

export type NotifyTemplateKey = keyof typeof NOTIFY_TEMPLATES;
export const NOTIFY_TEMPLATE_KEYS = Object.keys(NOTIFY_TEMPLATES) as NotifyTemplateKey[];
export type NotifyTemplateVars<K extends NotifyTemplateKey> = z.infer<(typeof NOTIFY_TEMPLATES)[K]>;

/** Body of the push-opened beacon: empty (the delivery id is in the path). */
export const DeliveryOpenedBody = z.object({}).strict();
